import { useState, useEffect, useCallback, useMemo } from 'react';
import { Diff, Hunk, parseDiff } from 'react-diff-view';
import { createGitDiff } from '../utils/contentDiff';
import 'react-diff-view/style/index.css';
import api from '../services/api';
import { useEscapeKey } from '../hooks/useEscapeKey';
import type {
  ContentVersionSummary,
  ContentVersionDetail,
  ContentVersionSource,
  ManagedPage,
} from '../types';

// Hook to detect if we're on mobile
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
}

interface VersionHistoryPanelProps {
  pageId: number;
  currentVersionId?: number | null;
  onRevert: (page?: ManagedPage) => void;
  // When the panel sits inside a dedicated settings section, collapsing
  // it would just put a click between the user and the data. Pass true
  // to render expanded from the start.
  defaultExpanded?: boolean;
}

const SOURCE_LABELS: Record<ContentVersionSource, { label: string; color: string }> = {
  BUILD: { label: 'Build', color: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300' },
  REFINE: { label: 'Refine', color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' },
  MANUAL_EDIT: { label: 'Manual Edit', color: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' },
  ACCEPT_FILE: { label: 'Accepted', color: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' },
  REVERT: { label: 'Revert', color: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' },
};

export default function VersionHistoryPanel({
  pageId,
  currentVersionId,
  onRevert,
  defaultExpanded = false,
}: VersionHistoryPanelProps) {
  const [versions, setVersions] = useState<ContentVersionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [selectedVersions, setSelectedVersions] = useState<number[]>([]);
  const [comparing, setComparing] = useState(false);
  const [comparisonData, setComparisonData] = useState<{
    a: ContentVersionDetail;
    b: ContentVersionDetail;
  } | null>(null);
  const [reverting, setReverting] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [viewingVersion, setViewingVersion] = useState<ContentVersionDetail | null>(null);
  const [loadingVersion, setLoadingVersion] = useState<number | null>(null);

  const isMobile = useIsMobile();
  const [viewType, setViewType] = useState<'split' | 'unified'>(isMobile ? 'unified' : 'split');

  // Auto-switch to unified view on mobile
  useEffect(() => {
    if (isMobile) {
      setViewType('unified');
    }
  }, [isMobile]);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getContentVersions(pageId);
      setVersions(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to load versions:', err);
    } finally {
      setLoading(false);
    }
  }, [pageId]);

  useEffect(() => {
    if (expanded) {
      loadVersions();
    }
  }, [expanded, currentVersionId, loadVersions]);

  const handleViewVersion = async (versionId: number) => {
    setLoadingVersion(versionId);
    try {
      const detail = await api.getContentVersion(pageId, versionId);
      setViewingVersion(detail);
    } catch (err) {
      console.error('Failed to load version:', err);
    } finally {
      setLoadingVersion(null);
    }
  };

  const handleCompare = async () => {
    if (selectedVersions.length !== 2) return;

    setComparing(true);
    try {
      const data = await api.compareVersions(pageId, selectedVersions[0], selectedVersions[1]);
      setComparisonData({ a: data.version_a, b: data.version_b });
    } catch (err) {
      console.error('Failed to compare versions:', err);
    } finally {
      setComparing(false);
    }
  };

  const handleRevert = async (versionId: number) => {
    if (
      !window.confirm(
        'Switch to this version? This will make it the current version.'
      )
    ) {
      return;
    }

    setReverting(versionId);
    try {
      const response = await api.switchToVersion(pageId, versionId);
      await loadVersions();
      onRevert(response.page);
    } catch (err) {
      console.error('Failed to switch version:', err);
      alert('Failed to switch to version');
    } finally {
      setReverting(null);
    }
  };

  const handleDelete = async (versionId: number, versionNumber: number, isCurrent: boolean) => {
    const message = isCurrent
      ? `Delete version ${versionNumber}? Since this is the current version, the page will switch to the most recently created remaining version.`
      : `Delete version ${versionNumber}? This cannot be undone.`;

    if (!window.confirm(message)) {
      return;
    }

    setDeleting(versionId);
    try {
      await api.deleteContentVersion(pageId, versionId);
      await loadVersions();
      // If we deleted the current version, refresh the page data
      if (isCurrent) {
        onRevert();
      }
    } catch (err) {
      console.error('Failed to delete version:', err);
      alert('Failed to delete version');
    } finally {
      setDeleting(null);
    }
  };

  const toggleVersionSelection = (versionId: number) => {
    setSelectedVersions((prev) => {
      if (prev.includes(versionId)) {
        return prev.filter((id) => id !== versionId);
      }
      if (prev.length >= 2) {
        return [prev[1], versionId];
      }
      return [...prev, versionId];
    });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const formatBuildDuration = (seconds: number) => {
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds - minutes * 60;
      const minuteLabel = `${minutes}m`;
      if (remainingSeconds < 0.05) {
        return `${minuteLabel}`;
      }
      const secondsLabel = `${remainingSeconds.toFixed(1).replace(/\.0$/, '')}s`;
      return `${minuteLabel} ${secondsLabel}`;
    }
    return `${seconds.toFixed(1)}s`;
  };

  // Generate diff files for react-diff-view
  const diffFiles = useMemo(() => {
    if (!comparisonData) return [];

    const oldContent = comparisonData.a.content || '';
    const newContent = comparisonData.b.content || '';

    // If both are identical, return empty
    if (oldContent === newContent) {
      return [];
    }

    try {
      // Create a git-style unified diff
      const diffText = createGitDiff(oldContent, newContent);

      if (!diffText) {
        return [];
      }

      // Parse the diff
      const files = parseDiff(diffText);

      // Filter out any files that don't have valid hunks
      return files.filter(file => file.hunks && file.hunks.length > 0);
    } catch (err) {
      console.error('Failed to parse diff:', err);
      return [];
    }
  }, [comparisonData]);

  // <Esc> closes the view-version and comparison modals.
  useEscapeKey(viewingVersion !== null, () => setViewingVersion(null));
  useEscapeKey(comparisonData !== null, () => setComparisonData(null));

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 mb-2"
      >
        <svg
          className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Version History
        {total > 0 && (
          <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
            {total} versions
          </span>
        )}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {loading ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">Loading versions...</div>
          ) : versions.length === 0 ? (
            <div className="text-sm text-gray-400 dark:text-gray-500 italic">No versions yet</div>
          ) : (
            <>
              {/* Compare controls */}
              {selectedVersions.length > 0 && (
                <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/30 rounded-md">
                  <span className="text-sm text-blue-700 dark:text-blue-300">{selectedVersions.length} selected</span>
                  {selectedVersions.length === 2 && (
                    <button
                      onClick={handleCompare}
                      disabled={comparing}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {comparing ? 'Comparing...' : 'Compare'}
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedVersions([])}
                    className="px-3 py-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    Clear
                  </button>
                </div>
              )}

              {/* Version list */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {versions.map((version) => {
                  const sourceInfo = version.source ? SOURCE_LABELS[version.source] : null;
                  const isSelected = selectedVersions.includes(version.id);

                  return (
                    <div
                      key={version.id}
                      className={`flex items-center justify-between p-3 rounded-md border ${
                        version.is_current
                          ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30'
                          : isSelected
                            ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleVersionSelection(version.id)}
                          className="h-4 w-4 text-blue-600 dark:text-blue-400 rounded"
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">v{version.version_number}</span>
                            {sourceInfo && (
                              <span
                                className={`px-2 py-0.5 text-xs rounded-full ${sourceInfo.color}`}
                              >
                                {sourceInfo.label}
                              </span>
                            )}
                            {version.is_current && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
                                Current
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {version.source_description || version.title || 'No description'}
                          </div>
                          <div className="text-xs text-gray-400 dark:text-gray-500">
                            {formatDate(version.created_at)}
                            {version.created_by_name && ` by ${version.created_by_name}`}
                            {version.content_length &&
                              ` · ${version.content_length.toLocaleString()} chars`}
                            {version.build_duration_seconds != null &&
                              ` · ${formatBuildDuration(version.build_duration_seconds)} build`}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewVersion(version.id)}
                          disabled={loadingVersion === version.id}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50"
                        >
                          {loadingVersion === version.id ? 'Loading...' : 'View'}
                        </button>
                        {!version.is_current && (
                          <button
                            onClick={() => handleRevert(version.id)}
                            disabled={reverting === version.id}
                            className="text-xs text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 disabled:opacity-50"
                          >
                            {reverting === version.id ? 'Switching...' : 'Use'}
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(version.id, version.version_number, version.is_current)}
                          disabled={deleting === version.id || versions.length <= 1}
                          className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 disabled:opacity-50"
                          title={versions.length <= 1 ? 'Cannot delete the only version' : 'Delete this version'}
                        >
                          {deleting === version.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* View Version Modal */}
          {viewingVersion && (
            <div className="fixed inset-0 z-50 overflow-hidden">
              <div
                className="absolute inset-0 bg-black bg-opacity-50"
                onClick={() => setViewingVersion(null)}
              />
              <div className="absolute inset-4 sm:inset-8 lg:inset-16 bg-white dark:bg-gray-800 rounded-lg shadow-xl flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-medium">
                    Version {viewingVersion.version_number}
                    {viewingVersion.is_current && (
                      <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
                        Current
                      </span>
                    )}
                  </h3>
                  <button
                    onClick={() => setViewingVersion(null)}
                    className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <div className="flex-1 overflow-auto p-4 bg-gray-50 dark:bg-gray-800">
                  <pre className="text-sm font-mono whitespace-pre-wrap">
                    {viewingVersion.content}
                  </pre>
                </div>
              </div>
            </div>
          )}

          {/* Comparison Modal */}
          {comparisonData && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-8">
              <div
                className="absolute inset-0 bg-black bg-opacity-50"
                onClick={() => setComparisonData(null)}
              />
              <div className="relative w-full h-full max-w-7xl bg-white dark:bg-gray-800 rounded-lg shadow-xl flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex-shrink-0 flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                  <h3 className="font-medium text-sm sm:text-base truncate">
                    v{comparisonData.a.version_number} → v{comparisonData.b.version_number}
                  </h3>
                  <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                    {/* View toggle */}
                    <div className="flex items-center gap-1 sm:gap-2 text-sm">
                      <button
                        onClick={() => setViewType('split')}
                        disabled={isMobile}
                        className={`px-2 py-1 rounded text-xs sm:text-sm ${
                          viewType === 'split'
                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        } ${isMobile ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title={isMobile ? 'Split view not available on mobile' : 'Split view'}
                      >
                        Split
                      </button>
                      <button
                        onClick={() => setViewType('unified')}
                        className={`px-2 py-1 rounded text-xs sm:text-sm ${
                          viewType === 'unified'
                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        Unified
                      </button>
                    </div>
                    <button
                      onClick={() => setComparisonData(null)}
                      className="p-1 sm:p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                {/* Diff content */}
                <div className="flex-1 overflow-auto min-h-0">
                  {diffFiles.length > 0 ? (
                    diffFiles.map((file, index) => (
                      <Diff
                        key={index}
                        viewType={isMobile ? 'unified' : viewType}
                        diffType={file.type}
                        hunks={file.hunks || []}
                        gutterType={isMobile ? 'none' : 'default'}
                      >
                        {(hunks) => hunks.map((hunk) => (
                          <Hunk key={hunk.content} hunk={hunk} />
                        ))}
                      </Diff>
                    ))
                  ) : (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                      No differences found between these versions
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
